import { Activity, Sun } from "lucide-react";
import { useTranslate } from "ra-core";
import { useRef, type KeyboardEvent } from "react";

import { HOME_VIEWS, useHomeView, type HomeView } from "./homeView";

const ICONS: Record<HomeView, typeof Sun> = { today: Sun, pulse: Activity };

/**
 * «Hoy | Pulso» segmented control. A radiogroup: arrows move and select,
 * Home / End jump. The thumb slides with the desk snappy spring (see
 * .home-toggle in styles/home.css; instant when the user prefers reduced
 * motion).
 */
export const HomeViewSegments = ({
  value: view,
  onChange: setView,
  label,
}: {
  value: HomeView;
  onChange: (view: HomeView) => void;
  label: string;
}) => {
  const translate = useTranslate();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const index = HOME_VIEWS.indexOf(view);

  const select = (next: number) => {
    setView(HOME_VIEWS[next]);
    buttons.current[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = HOME_VIEWS.length - 1;
    const moves: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowDown: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      ArrowUp: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const next = moves[event.key];
    if (next == null) return;
    event.preventDefault();
    select(next);
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="home-toggle"
      onKeyDown={onKeyDown}
    >
      <span
        aria-hidden
        className="home-toggle__thumb"
        style={{ transform: `translateX(${index * 100}%)` }}
      />
      {HOME_VIEWS.map((option, position) => {
        const Icon = ICONS[option];
        const active = option === view;
        return (
          <button
            key={option}
            ref={(node) => {
              buttons.current[position] = node;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            data-active={active}
            className="home-toggle__option"
            onClick={() => select(position)}
          >
            <Icon aria-hidden className="size-3.5" strokeWidth={1.75} />
            {translate(`desk.home.views.${option}`)}
          </button>
        );
      })}
    </div>
  );
};

/** The toggle of the desktop home header: this session's view. */
export const HomeViewToggle = () => {
  const translate = useTranslate();
  const [view, setView] = useHomeView();
  return (
    <HomeViewSegments
      value={view}
      onChange={setView}
      label={translate("desk.home.view_label")}
    />
  );
};
